import {
  commentCount,
  isTaskDone,
  taskMax,
  taskScore,
  totalMax,
  totalScore,
} from '../../domain/scoring';
import { toNumber } from '../../domain/points';
import { usePoints, useT } from '../../i18n';
import {
  selectAssessment,
  requireCandidate,
  selectStation,
  selectTasks,
  useAppStore,
} from '../../store/useAppStore';
import { punktfeldSichtbar } from '../../domain/bogenAnzeige';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import styles from './Submit.module.css';

/** Übersicht vor dem Absenden: was steht auf dem Bogen, und was passiert danach. */
export function SubmitDialog() {
  const t = useT();
  const points = usePoints();
  const tasks = useAppStore(selectTasks);
  const assessment = useAppStore(selectAssessment);
  const candidate = useAppStore(requireCandidate);
  const station = useAppStore(selectStation);
  const anzeige = useAppStore((s) => s.bogenAnzeige);
  const closeSheet = useAppStore((s) => s.closeSheet);
  const submit = useAppStore((s) => s.submitAssessment);

  const total = toNumber(totalScore(tasks, assessment));
  const max = toNumber(totalMax(tasks));
  const openTasks = tasks.filter((t) => !isTaskDone(t, assessment));

  // Der Abzug entsteht immer (Vorgabe des Nutzers, 2026-09-17) — sein Hinweis steht deshalb
  // ohne Bedingung da. Was danach kommt, zeigt der Bestätigungsdialog mit seinen Knöpfen; die
  // Sätze „Danach öffnet …“ beschrieben einen Ablauf, den es nie gab (entfallen 2026-10-02).
  const note = t.submit.screenshotNote;

  return (
    <Dialog
      kicker={t.submit.kicker}
      title={candidate.name + ' · ' + station.label}
      onClose={closeSheet}
      width={640}
    >
      <div className={`sc ${styles.rows}`}>
        {tasks.map((task, i) => {
          const comments = commentCount(assessment.comments[task.id]);
          return (
            <div key={task.id} className={styles.row}>
              <span className={styles.rowNumber}>{i + 1}</span>
              <span className={styles.rowTitle}>{task.short}</span>
              <span className={styles.rowComments}>
                {comments === 0 ? t.submit.noComment : t.bogen.comments(comments)}
              </span>
              {punktfeldSichtbar(task, anzeige) && (
                <span className={styles.rowScore}>
                  {points(toNumber(taskScore(task, assessment)))}
                  <span className={styles.rowComments}> / {points(toNumber(taskMax(task)))}</span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {anzeige.summe && (
        <div className={styles.total}>
          <span className={styles.totalLabel}>{t.submit.totalLabel}</span>
          <span className={styles.totalValue}>
            {points(total)} / {points(max)}
          </span>
        </div>
      )}

      {/*
       * Der Hinweis hält für sich genommen **nicht** auf. Eine unbewertete Aufgabe kann gewollt
       * sein — der Prüfling war nicht da, die Station wurde abgebrochen. Wer absendet, soll es
       * aber wissen.
       *
       * ⛔ `submit_require_complete` machte daraus eine Sperre; am 2026-10-02 entfallen
       * (Entscheidung des Nutzers): absenden geht immer.
       */}
      {openTasks.length > 0 && (
        <div className={styles.warning}>
          {t.submit.openTasks(openTasks.map((task) => task.short).join(', '), openTasks.length)}
        </div>
      )}

      <div className={styles.note}>{note}</div>

      <div className={styles.buttons}>
        <button type="button" className={`tap ${styles.cancel}`} onClick={closeSheet}>
          <Icon name="close" size={16} />
          <span>{t.submit.cancel}</span>
        </button>
        <button
          type="button"
          className={`tap ${styles.confirm}`}
          onClick={() => submit(total, max)}
        >
          <Icon name="send" size={16} />
          <span>{t.submit.confirm}</span>
        </button>
      </div>
    </Dialog>
  );
}
